import React, { useRef, useState, useEffect, useMemo } from "react";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Scrollbar, A11y } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import "./styles/QuickAddOns.css";
import quickAddOnService from "../../service/quickaddon.service";
import { QuickAddOnProductDto } from "../dto/quickaddon.dto";
import { useCart } from "../../../../contexts/CartContext";
import 'react-toastify/dist/ReactToastify.css';

interface QuickAddOnsProps {
  cartItems?: any[];
  limit?: number;
}

const QuickAddOns: React.FC<QuickAddOnsProps> = ({ cartItems = [], limit = 8 }) => {
  const swiperRef = useRef<any>(null);
  const [items, setItems] = useState<QuickAddOnProductDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const loadingRef = useRef<boolean>(false);
  const [addingToCart, setAddingToCart] = useState<Set<string>>(new Set());
  const { addToCart } = useCart();
  const cartItemIds = useMemo(() => {
    return cartItems.map(item => item.productId || item._id || item.id).filter(Boolean);
  }, [cartItems]);

  // Create a stable cart items array
  const stableCartItems = useMemo(() => cartItems, [JSON.stringify(cartItemIds)]);

  // Fetch quick add-on products
  useEffect(() => {
    const fetchQuickAddOnProducts = async () => {
      // Prevent concurrent requests
      if (loadingRef.current) return;
      
      try {
        loadingRef.current = true;
        setLoading(true);
        setIsError(false);
        setError(null);

        const response = await quickAddOnService.getQuickAddOnsFromCart(stableCartItems, limit);
        
        if (response?.success && Array.isArray(response.data)) {
          setItems(response.data);
        } else {
          setItems([]);
        }
      } catch (err) {
        setIsError(true);
        setError(err instanceof Error ? err : new Error('Unable to load quick add-on products. Please try again later.'));
        setItems([]);
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    };

    fetchQuickAddOnProducts();
  }, [stableCartItems, limit]);

  const handleAddToCart = async (product: QuickAddOnProductDto) => {
    // Check if product is out of stock
    const isOutOfStock = product.trackInventory && product.quantity === 0;
    if (isOutOfStock) return;

    const productId = product._id || product.id;
    if (!productId) return;

    // Prevent duplicate clicks
    if (addingToCart.has(productId)) return;

    setAddingToCart(prev => new Set(prev).add(productId));

    try {
      const cartProduct = {
        id: productId,
        _id: productId,
        name: product.title || product.name || 'Unnamed Product',
        title: product.title || product.name || 'Unnamed Product',
        price: product.price || 0,
        imageUrl: product.images?.[0]?.url || product.imageUrl || product.image || '',
        image: product.images?.[0]?.url || product.imageUrl || product.image || '',
        description: product.description || product.shortDescription || '',
        quantity: 1,
        slug: product.slug,
      };

      await addToCart(cartProduct, 1);
    } catch (error) {
    } finally {
      setAddingToCart(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }
  };

  if (loading) {
    return (
      <div className="quick-addons">
        <h4>Quick Add–Ons in 30 Minutes</h4>
        <div className="swiper-container">
          <div className="addons-list">
            {Array(4).fill(0).map((_, index) => (
              <div key={index} className="addon-card animate-pulse">
                <div className="addon-img-container bg-gray-200"></div>
                <div className="addon-name bg-gray-200 h-4 rounded"></div>
                <div className="addon-price bg-gray-200 h-4 rounded w-3/4"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  if (isError) {
    return (
      <div className="quick-addons">
        <h4>Quick Add–Ons in 30 Minutes</h4>
        <div className="text-center py-4">
          <p className="text-sm text-gray-500">
            {error?.message || 'Unable to load quick add-on products'}
          </p>
        </div>
      </div>
    );
  }
  if (items.length === 0) {
    return null;
  }

  return (
    <div className="quick-addons">
      <h4>Quick Add–Ons in 30 Minutes</h4>
      <div className="swiper-container">
        <Swiper
          modules={[Navigation, Pagination, Scrollbar, A11y]}
          spaceBetween={15}
          slidesPerView={'auto'}
          navigation
          pagination={{ clickable: true }}
          scrollbar={{ draggable: true }}
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          className="addons-list"
        >
          {items.map((item: QuickAddOnProductDto, index: number) => {
            const displayImage = item.images?.[0]?.url || 
                                item.imageUrl || 
                                item.image || 
                                "https://ik.imagekit.io/raghav11/download.jpg?updatedAt=1757934662868";
            const productId = item._id || item.id;
            const isAdding = productId ? addingToCart.has(productId) : false;
            const isOutOfStock = item.trackInventory && item.quantity === 0;
            const roundedPrice = Math.round(item.price || 0);
            
            return (
              <SwiperSlide key={item._id || index} className="swiper-slide">
                <div className="addon-card" style={{ position: 'relative' }}>
                  {isOutOfStock && (
                    <div style={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      background: '#ccc',
                      color: '#fff',
                      padding: '2px 8px',
                      borderRadius: '8px',
                      fontSize: '0.8rem',
                      zIndex: 20
                    }}>
                      Out of Stock
                    </div>
                  )}
                  <button 
                    className="addon-add-btn" 
                    onClick={() => handleAddToCart(item)}
                    disabled={isAdding || isOutOfStock}
                    title={isOutOfStock ? "Out of Stock" : "Add to Cart"}
                    style={isOutOfStock ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                  >
                    {isAdding ? (
                      <div className="spinner" />
                    ) : (
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      >
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                    )}
                  </button>
                  <div className="addon-img-container">
                    <img
                      src={displayImage}
                      alt={item.title || item.name || 'Product'}
                      className="addon-img"
                      onError={(e) => {
                        e.currentTarget.src = "https://ik.imagekit.io/raghav11/download.jpg?updatedAt=1757934662868";
                      }}
                    />
                  </div>
                  <p className="addon-name">{item.title || item.name}</p>
                  <p className="addon-price">₹ {roundedPrice.toLocaleString()}</p>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    </div>
  );
};

export default QuickAddOns;
